import { fetchListingOffersById, fetchOffersById } from 'actions/OfferActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useStableObjectReference from 'hooks/useStableObjectReference'
import { useEffect, useMemo } from 'react'
import { getAllOffers, getIsListingOfferEnabledForCurrentPage } from 'selectors/offerSelectors'

interface Options {
  preferListingOffers?: boolean
}

function useOffers<T extends App.OfferBase = App.AnyOffer>(offerIds: Array<string>, options: Options = {}): [Array<T>, boolean] {
  const dispatch = useAppDispatch()
  const listingPayloadEnabled = useAppSelector(getIsListingOfferEnabledForCurrentPage)
  const useListingEndpoint = !!(listingPayloadEnabled && options.preferListingOffers)

  const stableOfferIds = useStableObjectReference(offerIds)

  useEffect(() => {
    if (useListingEndpoint) {
      dispatch(fetchListingOffersById(stableOfferIds))
    } else {
      dispatch(fetchOffersById(stableOfferIds))
    }
  }, [stableOfferIds, dispatch, useListingEndpoint])

  const allOffers = useAppSelector(getAllOffers)
  const listingOffers = useAppSelector(state => useListingEndpoint ? state.offer.listingOffers : undefined)
  const offersLoading = useAppSelector(state => useListingEndpoint ? state.offer.listingOffersLoading : state.offer.offersLoading)
  const offersFetching = useMemo(() => stableOfferIds.some(id => offersLoading[id]), [offersLoading, stableOfferIds])

  const offers = useMemo((): Array<T> =>
    stableOfferIds.map<any>(id => listingOffers?.[id] ?? allOffers[id]).filter(Boolean),
  [stableOfferIds, allOffers, listingOffers])

  return [
    offers,
    offersFetching,
  ]
}

export default useOffers
